import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Space,
  Typography,
  Alert,
  Tooltip,
} from 'antd';
import {
  LinkOutlined,
  CopyOutlined,
  MailOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { theme } from '../constants/theme';
import { apiRequest } from '../helpers/api';

const { Title, Text, Paragraph } = Typography;

const useStyles = createUseStyles({
  modal: {
    '& .ant-modal-content': {
      borderRadius: '12px',
      padding: '24px',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  title: {
    fontFamily: "'Baloo 2'",
    fontWeight: '600',
    fontSize: '24px !important',
    color: theme.palette.primary,
    marginBottom: '8px !important',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '16px !important',
  },
  form: {
    '& .ant-form-item-label > label': {
      fontWeight: '500',
      fontSize: '14px',
    },
  },
  paymentLinkResult: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
  },
  linkContainer: {
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '12px',
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  linkText: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#374151',
    wordBreak: 'break-all',
    flex: 1,
  },
  actionButtons: {
    marginTop: '24px',
    textAlign: 'center',
    '& .ant-btn': {
      marginLeft: '8px',
      marginRight: '8px',
    },
  },
  generateButton: {
    backgroundColor: theme.palette.primary,
    borderColor: theme.palette.primary,
    fontWeight: '600',
    height: '40px',
    '&:hover': {
      backgroundColor: '#3730a3',
      borderColor: '#3730a3',
    },
  },
  copyButton: {
    borderColor: theme.palette.primary,
    color: theme.palette.primary,
    '&:hover': {
      borderColor: '#3730a3',
      color: '#3730a3',
    },
  },
  infoAlert: {
    marginBottom: '16px',
    '& .ant-alert-message': {
      fontSize: '14px',
    },
  },
  formItem: {
    marginBottom: '16px',
  },
});

interface PaymentLinkModalProps {
  visible: boolean;
  onCancel: () => void;
  projectId: string;
  projectName: string;
  type: 'ngo' | 'donor';
  offer: string;
  pricing: string;
  isMonthly: string;
}

interface PaymentLinkData {
  token: string;
  paymentUrl: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  message?: string;
}

export const PaymentLinkModal: React.FC<PaymentLinkModalProps> = ({
  visible,
  onCancel,
  projectId,
  projectName,
  type,
  offer,
  pricing,
  isMonthly,
}) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState<PaymentLinkData | null>(null);
  const [savedFormData, setSavedFormData] = useState<FormData | null>(null);

  const handleGenerateLink = async (values: any) => {
    setLoading(true);
    try {
      // Save form data for email generation
      setSavedFormData({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        message: values.message,
      });

      const data = await apiRequest<PaymentLinkData>(
        'POST',
        '/payment/generatePaymentLink',
        undefined,
        {
          projectId,
          type,
          offer,
          pricing,
          isMonthly,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          message: values.message,
          expirationDate: values.expirationDate,
        },
      );

      setPaymentLink(data);
      message.success('Lien de paiement généré avec succès !');
    } catch (error) {
      console.error('Error generating payment link:', error);
      message.error(
        error instanceof Error
          ? error.message
          : 'Impossible de générer le lien de paiement',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink.paymentUrl);
      message.success('Lien de paiement copié dans le presse-papiers !');
    }
  };

  const handleEmailLink = () => {
    if (paymentLink && savedFormData) {
      const recipientEmail = savedFormData.email;
      const recipientName =
        savedFormData.firstName && savedFormData.lastName
          ? `${savedFormData.firstName} ${savedFormData.lastName}`
          : 'there';

      const subject = encodeURIComponent(
        `Demande de paiement pour ${projectName}`,
      );

      // Simplified email body to avoid email client limitations
      const bodyLines = [
        `Bonjour ${recipientName},`,
        '',
        `Vous avez été invité(e) à soutenir le projet "${projectName}" sur Sonar.`,
      ];

      if (savedFormData.message) {
        bodyLines.push('');
        bodyLines.push(`"${savedFormData.message}"`);
      }

      bodyLines.push(
        '',
        'Veuillez cliquer sur le lien suivant pour effectuer un paiement sécurisé :',
        '',
        paymentLink.paymentUrl,
        '',
        'Ce lien de paiement est sécurisé et expire dans 30 jours.',
        '',
        'Merci pour votre soutien !',
        '',
        'Cordialement',
      );

      // Create a simplified body for better email client compatibility
      const simpleBodyLines = [
        `Bonjour ${recipientName},`,
        '',
        `Lien de paiement pour "${projectName}" :`,
        paymentLink.paymentUrl,
        '',
        'Merci !',
      ];

      const simpleBody = encodeURIComponent(simpleBodyLines.join('\n'));
      const mailtoUrl = `mailto:${recipientEmail}?subject=${subject}&body=${simpleBody}`;

      // Always try to open the email client
      window.open(mailtoUrl);

      // Also copy the full content to clipboard as backup
      const fullEmailContent = bodyLines.join('\n');
      navigator.clipboard.writeText(fullEmailContent);
      message.success(
        'Email ouvert et contenu complet copié dans le presse-papiers !',
      );
    }
  };

  const handleModalClose = () => {
    form.resetFields();
    setPaymentLink(null);
    setSavedFormData(null);
    onCancel();
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleModalClose}
      footer={null}
      width={600}
      className={classes.modal}
    >
      <div className={classes.header}>
        <Title level={3} className={classes.title}>
          <LinkOutlined /> Générer un lien de paiement
        </Title>
        <Text className={classes.subtitle}>
          Créez un lien de paiement sécurisé à partager avec quelqu&apos;un qui
          paiera votre abonnement premium
        </Text>
      </div>

      <Alert
        message="Comment ça fonctionne"
        description="La personne avec qui vous partagez ce lien pourra payer votre abonnement premium en votre nom. Une fois le paiement effectué, votre projet recevra automatiquement les fonctionnalités premium."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        className={classes.infoAlert}
      />

      {!paymentLink ? (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerateLink}
          className={classes.form}
        >
          <Form.Item
            label="Prénom"
            name="firstName"
            rules={[
              { required: true, message: 'Veuillez saisir le prénom' },
              {
                max: 50,
                message: 'Le prénom doit contenir moins de 50 caractères',
              },
              {
                pattern: /^[a-zA-ZÀ-ÿ\s\-']+$/,
                message: 'Le prénom contient des caractères invalides',
              },
            ]}
            className={classes.formItem}
          >
            <Input placeholder="Saisissez le prénom du payeur" />
          </Form.Item>

          <Form.Item
            label="Nom"
            name="lastName"
            rules={[
              { required: true, message: 'Veuillez saisir le nom' },
              {
                max: 50,
                message: 'Le nom doit contenir moins de 50 caractères',
              },
              {
                pattern: /^[a-zA-ZÀ-ÿ\s\-']+$/,
                message: 'Le nom contient des caractères invalides',
              },
            ]}
            className={classes.formItem}
          >
            <Input placeholder="Saisissez le nom du payeur" />
          </Form.Item>

          <Form.Item
            label="Adresse email"
            name="email"
            rules={[
              { required: true, message: "Veuillez saisir l'adresse email" },
              {
                type: 'email',
                message: 'Veuillez saisir une adresse email valide',
              },
            ]}
            className={classes.formItem}
          >
            <Input placeholder="Saisissez l'adresse email du payeur" />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Message personnel{' '}
                <Tooltip title="Message optionnel à inclure avec le lien de paiement">
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </span>
            }
            name="message"
            rules={[
              {
                max: 500,
                message: 'Le message doit contenir moins de 500 caractères',
              },
            ]}
            className={classes.formItem}
          >
            <Input.TextArea
              rows={3}
              placeholder="Optionnel : Ajoutez un message personnel pour expliquer pourquoi vous demandez ce paiement"
              showCount
              maxLength={500}
            />
          </Form.Item>

          <div className={classes.actionButtons}>
            <Button onClick={handleModalClose}>Annuler</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className={classes.generateButton}
            >
              Générer le lien de paiement
            </Button>
          </div>
        </Form>
      ) : (
        <div className={classes.paymentLinkResult}>
          <Title level={4} style={{ color: '#059669', marginBottom: '16px' }}>
            ✅ Lien de paiement généré avec succès !
          </Title>

          <Paragraph>
            Votre lien de paiement sécurisé a été créé. Partagez ce lien avec la
            personne qui effectuera le paiement :
          </Paragraph>

          <div className={classes.linkContainer}>
            <Text className={classes.linkText}>{paymentLink.paymentUrl}</Text>
            <Space>
              <Tooltip title="Copier le lien">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={handleCopyLink}
                  className={classes.copyButton}
                />
              </Tooltip>
              <Tooltip title="Envoyer par email">
                <Button
                  type="text"
                  icon={<MailOutlined />}
                  onClick={handleEmailLink}
                  className={classes.copyButton}
                />
              </Tooltip>
            </Space>
          </div>

          <Alert
            message="Informations de sécurité importantes"
            description="Ce lien expirera dans 30 jours et ne peut être utilisé qu'une seule fois. Gardez-le sécurisé et ne le partagez qu'avec des personnes de confiance."
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />

          <div className={classes.actionButtons}>
            <Button onClick={handleModalClose}>Fermer</Button>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={handleCopyLink}
              className={classes.generateButton}
            >
              Copier le lien
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
